import * as React from "react";
import { api, closesOn, revealRows, TEXT_MAX, type MyBoard, type QuestionAggregate } from "../api.js";
import { getLocale, t } from "../i18n.js";
import { Shell, LiveRegion, OmittedNote, OpenEntryText, WithheldNote } from "../components.js";
import { describeAggregate, describeLeaderboard } from "../announce.js";
import { useRoom, ROUND_CLOSED_UNSENT } from "../useRoom.js";
import { joinBackoffMs, shouldRetryJoin } from "../join-retry.js";
import { queueBanner, wordsUsed } from "../queue.js";

/**
 * What this phone needs to be the SAME person after a reload (W7 review, HIGH-2).
 *
 * Before this, token and pid lived only in React state. A reload meant a fresh `POST /join`, a fresh
 * token and a fresh pid — so a round scoped to the pid the host chose became unanswerable by the
 * person holding the phone, and their team chip vanished. The server side of the fix is that a
 * returning token keeps its public id; this is the half that lets the token return at all.
 */
export interface Seat {
  token: string;
  /** The public id from `/join`. Kept for parity with the join response; the wire never carries it. */
  pid: string | null;
  nickname: string;
  connectPath: string;
}

/**
 * `sessionStorage` for a live workshop, `localStorage` for a room that runs for days (W11).
 *
 * The original reasoning still holds where it was written: a seat belongs to this tab and this room,
 * a closed tab is a person who has left, and a shared laptop must not hand the next room's
 * participant somebody else's token. It is right for a two-hour workshop and WRONG for homework —
 * in a session that spans a fortnight, closing the tab on Monday is not leaving, and a seat that
 * dies with the tab means the same person comes back on Wednesday as a stranger with no team and no
 * points. So the store is chosen by what the room actually is, and the persistent one is paired with
 * an explicit "sair desta sala" plus a stated horizon on the join screen — a persistent seat on a
 * shared phone needs a way out and a promise about how long it lasts.
 *
 * Keyed by JOIN CODE either way, because that is the only room identifier this component has before
 * it joins — one live code addresses one session, so it is the same key by another name.
 */
const SEAT_KEY = "klapzo.seat.";

/** Every access is guarded: either store is absent under the prerender pass and vitest, and THROWS
 *  on access in a browser with site data blocked. Same guard `i18n.resolveLocale` uses. */
function storage(persistent = false): Storage | null {
  try {
    if (persistent) return typeof localStorage === "undefined" ? null : localStorage;
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/**
 * The stored seat, from EITHER store — session first, then local.
 *
 * Reading both is what removes the ordering problem entirely: at mount this component has not called
 * the API yet, so it cannot know whether the room is long-running, and a reader that had to be told
 * would either miss the seat or need a round-trip before it could restore one. Two lines instead.
 */
export function readSeat(code: string): Seat | null {
  return readFrom(code, false) ?? readFrom(code, true);
}

/**
 * Did the seat come back from the PERSISTENT store — i.e. is this a room that runs for days?
 *
 * Known synchronously, which is the point (round-2 review). `joined` is initialised from `readSeat`
 * at mount, but `longRunning` waited on `GET /j/:code`, so a restored seat mounted the room hook with
 * `persistent=false` and its first effect moved the offline queue localStorage → sessionStorage — back
 * again one round-trip later when the resolve landed, and NEVER when it did not: on a return visit
 * with the API unreachable, Monday's unsent frames ended up in the store that dies with the tab
 * (round-3 review; that is what makes this a fix rather than a tightening). The store the seat was
 * found in already answers the question the fetch was being waited on for. The join response stays
 * the authority — it is what MINTS a seat, and it corrects this the moment it lands.
 */
export function seatIsPersistent(code: string): boolean {
  return readFrom(code, false) === null && readFrom(code, true) !== null;
}

function readFrom(code: string, persistent: boolean): Seat | null {
  try {
    const raw = storage(persistent)?.getItem(SEAT_KEY + code);
    if (!raw) return null;
    const seat = JSON.parse(raw) as Partial<Seat>;
    // A half-written or hand-edited entry is WORSE than none: it would skip the join form and then
    // never connect, leaving someone staring at a blank screen with no way back to the code.
    if (typeof seat.token !== "string" || !seat.token) return null;
    if (typeof seat.connectPath !== "string" || !seat.connectPath) return null;
    if (typeof seat.nickname !== "string") return null;
    return {
      token: seat.token,
      pid: typeof seat.pid === "string" ? seat.pid : null,
      nickname: seat.nickname,
      connectPath: seat.connectPath,
    };
  } catch {
    return null;
  }
}

/** `persistent` comes from the join response's `longRunning` — the room decides, not the client. */
export function writeSeat(code: string, seat: Seat, persistent = false): void {
  try {
    storage(persistent)?.setItem(SEAT_KEY + code, JSON.stringify(seat));
  } catch {
    // Quota, or a browser refusing site data. Losing the reload guarantee is not worth losing the join.
  }
}

/** Clears BOTH stores: the seat may have been written under a mode this tab no longer knows about. */
export function clearSeat(code: string): void {
  for (const persistent of [false, true]) {
    try {
      storage(persistent)?.removeItem(SEAT_KEY + code);
    } catch {
      /* nothing to undo */
    }
  }
}

/** The socket's two ways of saying "that seat is gone" — anything else is a refusal, not an eviction. */
const SEAT_LOST = new Set(["UNKNOWN_TOKEN", "INVALID_JOIN"]);

/**
 * Does this refusal mean the stored seat is dead (round-2 review, fix 2)?
 *
 * `ROOM_FULL` is the interesting one, because the server sends it for a full room AND for a CLOSED
 * one. On a FIRST join that is a "wait and retry" — the socket keeps its jittered backoff and gets in
 * when a seat frees. On a RESTORED seat it is a trap: the phone reconnects, is refused a seat,
 * receives every broadcast as a silent observer and can never answer anything, with no way back to
 * the join form. Clearing it costs one `POST /join`, which reports full-or-closed through the UI
 * that already handles both.
 */
export function seatIsLost(code: string, restored: boolean): boolean {
  return SEAT_LOST.has(code) || (restored && code === "ROOM_FULL");
}

export function Participant({ code }: { code: string }): React.ReactElement {
  const [nickname, setNickname] = React.useState("");
  // `pid` is this participant's own public id, minted with the token and returned by /join. It is
  // held here because this response is the ONLY copy: it is deliberately absent from every frame, so
  // a phone can know its own id and no one else's — which is what makes a targeted round targeted.
  // Restored from the tab's storage on mount, so a reload rejoins as the SAME person rather than
  // minting a new identity the host's targeting and eligibility lists have never heard of.
  const [joined, setJoined] = React.useState<Seat | null>(() => readSeat(code));
  // Was this seat RESTORED from storage, or minted just now? Only a restored one can be stale, and
  // only a stale one should read `ROOM_FULL` as "start over" rather than "wait your turn".
  const [restored, setRestored] = React.useState(() => joined !== null);
  const [error, setError] = React.useState<string | null>(null);
  const [submitted, setSubmitted] = React.useState(false);
  /**
   * Is this a room that runs for days (W11)?
   *
   * Resolved BEFORE the nickname is typed, because that is the whole point: a seat kept in this
   * browser for a fortnight is something the participant has to be told about while they can still
   * decide not to, not something they discover on Wednesday. It also gives `GET /j/:code` its first
   * caller — the resolve existed and nothing on this client used it.
   *
   * Seeded from the store the seat was restored from, so a returning phone does not spend a
   * round-trip with its offline queue in the wrong store (round-2 review). A failed resolve leaves
   * that seed in place — false for a phone with no seat, which is exactly today's screen: the join
   * itself is what decides the store, from its own response.
   */
  const [longRunning, setLongRunning] = React.useState(() => seatIsPersistent(code));
  /**
   * Until when the seat is kept, in unix seconds — 0 when unknown (AC7, round-1 review).
   *
   * The sentence AC7 asks for states a PERIOD, and the wire gave it nothing to state one with: the
   * screen said "until you leave or the host closes it", which is not a retention period. The room's
   * horizon is a public fact about the room — a participant deciding whether to leave a seat in this
   * browser for a fortnight is precisely who it is for — and it is rendered as a date in the phone's
   * own timezone, by the same formatter the round's close uses.
   */
  const [expiresAt, setExpiresAt] = React.useState(0);
  React.useEffect(() => {
    let live = true;
    void api
      .resolveCode(code)
      .then(({ status, data }) => {
        if (!live || status !== 200) return;
        setLongRunning(data.longRunning === true);
        setExpiresAt(data.expiresAt ?? 0);
      })
      // The Worker unreachable is a real Wednesday morning (5xx, captive portal): the screen stays
      // usable on the seed above, and an unhandled rejection is not how it says so.
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [code]);

  // Lazy import of the room hook only once joined keeps the join screen simple.
  if (!joined) {
    // `> 0` and not just "is it finite": 0 is this state's "not known yet", and `closesOn(0)` would
    // render 1 January 1970 as the retention period on somebody's phone. No date, no sentence — the
    // screen never states a period it does not have.
    const seatUntil = expiresAt > 0 ? closesOn(expiresAt * 1000, getLocale()) : null;
    const onJoin = async (): Promise<void> => {
      const nick = nickname.trim();
      if (!nick) return setError(t("join.nicknameRequired"));
      let res = await api.join(code, nick);
      // A venue Wi-Fi puts the whole room behind a few IPs (US-LV-26): a 429 is "busy", not "no".
      for (let attempt = 0; shouldRetryJoin(res.status, attempt); attempt++) {
        setError(t("join.busyRetrying"));
        await new Promise((r) => setTimeout(r, joinBackoffMs(attempt, Math.random())));
        res = await api.join(code, nick);
      }
      const { status, data } = res;
      setError(null);
      if (status === 429) return setError(t("join.rateLimited"));
      if (status === 404) return setError(t("join.notFound"));
      // A nickname refused for looking like personal data needs its own sentence: answering it with
      // "enter a nickname" invites the participant to retype the same phone number.
      if (data.error === "NICKNAME_LOOKS_LIKE_PII") return setError(t("join.nicknamePii"));
      if (status !== 200 || !data.participantToken || !data.connectPath)
        return setError(t("join.invalidCode"));
      const seat: Seat = {
        token: data.participantToken,
        pid: data.pid ?? null,
        nickname: nick,
        connectPath: data.connectPath,
      };
      // The ROOM decides which store, from the response that minted the seat — not from the resolve
      // above, which may not have landed, and never from a client-side guess.
      writeSeat(code, seat, data.longRunning === true);
      setLongRunning(data.longRunning === true);
      if (data.expiresAt !== undefined) setExpiresAt(data.expiresAt);
      setRestored(false);
      setJoined(seat);
    };
    return (
      <Shell bare>
        <h1 className="font-display text-display-md">{t("join.title")}</h1>
        <div className="rounded-md bg-surface p-3 text-center font-display text-display-sm tracking-widest">{code}</div>
        <label className="flex flex-col gap-2">
          <span className="text-body-sm">{t("join.nickname")}</span>
          <input
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void onJoin()}
            className="rounded-md border border-border p-3 text-body-lg"
            autoFocus
            maxLength={TEXT_MAX.nickname}
          />
        </label>
        {/* The retention period, stated where it can still be declined (#5 pairs "no PII unless
            flagged" with "export + delete supported", and a period the participant cannot see is a
            purpose-limitation problem, not a PR note). Said as a PERIOD and not a date: the
            participant wire deliberately carries no session expiry instant. */}
        {longRunning && (
          <p className="text-body-sm text-muted">
            {/* A room with NO horizon at all is long-running by construction (`isLongRunning` reads a
                null `expiresAt` as uncapped) and is the one whose retention is UNBOUNDED — so the
                dateless variant, never silence: the guard that asked for a date hid the sentence from
                exactly the row shape that most needs it (round-2 review). */}
            {seatUntil === null
              ? t("join.longRunningRoomNoDate")
              : t("join.longRunningRoom", { date: seatUntil })}
          </p>
        )}
        {error && <p className="text-warning-DEFAULT bg-warning rounded-md p-2 text-primary-foreground">{error}</p>}
        <button onClick={() => void onJoin()} className="rounded-md bg-primary p-4 font-bold text-primary-foreground">
          {t("join.cta")}
        </button>
      </Shell>
    );
  }
  return (
    <JoinedParticipant
      // From the SEAT, not from the input: after a reload the input is empty, and the socket `join`
      // carries whatever it is given — an empty nickname is refused as INVALID_JOIN.
      token={joined.token}
      nickname={joined.nickname}
      connectPath={joined.connectPath}
      submitted={submitted}
      setSubmitted={setSubmitted}
      restored={restored}
      longRunning={longRunning}
      onExit={() => {
        // A persistent seat needs a way out, and it is stated rather than hidden: leaving makes them
        // a NEW participant next time, with a new pid and no points. Both stores AND the offline
        // queue go — the queue holds frames minted under a token this browser is giving up.
        clearSeat(code);
        setJoined(null);
        setSubmitted(false);
        setError(null);
      }}
      onSeatLost={() => {
        // The room does not know this token any more (swept, closed, or a different session reusing
        // the code). Drop it and show the join form rather than retrying a credential that is dead.
        clearSeat(code);
        setJoined(null);
        // NOT "código inválido": the overwhelmingly common cause is a token older than its 10-minute
        // window, and the code the participant is looking at is perfectly good. Telling them it is
        // wrong sends them to find another one that does not exist.
        setError(t("join.seatExpired"));
      }}
    />
  );
}

// Separate component so the room hook mounts only after join.
function JoinedParticipant(props: {
  token: string;
  nickname: string;
  connectPath: string;
  submitted: boolean;
  setSubmitted: (b: boolean) => void;
  /** See `seatIsLost` — a restored seat reads `ROOM_FULL` differently from a fresh one. */
  restored: boolean;
  /** A room that runs for days (W11): the seat persists, so the screen owes an exit and a come-back. */
  longRunning: boolean;
  onExit: () => void;
  onSeatLost: () => void;
}): React.ReactElement {
  // The queue goes in the SAME store as the seat (round-1 review) — see `persist` in queue.ts.
  const { state, send, clearQueue } = useRoom(props.connectPath, {
    onOpen: () => send({ t: "join", token: props.token, nickname: props.nickname }),
    persistent: props.longRunning,
  });
  const onSeatLost = props.onSeatLost;
  const onExit = props.onExit;
  const restored = props.restored;
  // A restored seat can be refused — the token's window was swept, the room filled up, or the code
  // now addresses another room. Only a seat-level refusal counts; every other one is about the
  // submission, and clearing on one of those would eject someone mid-round for a rejected answer.
  React.useEffect(() => {
    if (state.lastError === null || !seatIsLost(state.lastError, restored)) return;
    // The queue goes with the seat (W10b, AC4): the next join mints a NEW token, and replaying
    // these frames under it would attribute one person's answers to another identity.
    clearQueue();
    onSeatLost();
  }, [state.lastError, restored, onSeatLost, clearQueue]);
  const q = state.question;
  const [word, setWord] = React.useState("");
  const [answer, setAnswer] = React.useState("");

  React.useEffect(() => props.setSubmitted(false), [q?.id]); // reset on new question

  // The phone is where a screen-reader user actually is, so this is the load-bearing live region.
  const spoken = state.leaderboard.length > 0 || state.teamBoard.length > 0
    ? describeLeaderboard(state.leaderboard, state.teamBoard)
    : describeAggregate(state.aggregate, { options: q?.options ?? [], question: q, closed: state.closed });

  // What the phone may honestly say about answers it is holding (W10b, US-LV-18 AC5). Null means
  // "say nothing", which leaves the existing `participant.submitted` exactly as it was.
  const banner = state.roomFull ? null : queueBanner(state.connected, state.offline, state.pending);

  return (
    <Shell bare>
      <LiveRegion text={spoken} />
      <TeamStatus team={state.team} board={state.myBoard} />
      {state.race && (
        <p className="text-sm font-bold text-muted">
          {state.race.ended
            ? t("presenter.raceEnded")
            : `${t("presenter.raceRound")} ${state.race.round}/${state.race.total}`}
        </p>
      )}
      {/* "Aguardando o facilitador" and "this phone has no connection at all" looked identical
          before W10b, and only one of them is something the participant can act on. The banner is
          the one that says so, so the neutral line steps aside rather than saying it twice. */}
      {state.roomFull && <p className="text-accent">{t("participant.roomFull")}</p>}
      {!q && !banner && !state.roomFull && <p className="text-body-lg">{t("participant.waiting")}</p>}
      {/* THE refusal line, for every round type — see `RefusalNotice`. It lives here, once, above
          the type branches, and that placement is the fix: it used to live inside `PromptVote`, so
          a word cloud's or a Q&A round's refusal set `lastError` and nothing on the screen ever
          read it. A participant whose word was rejected saw the box empty and no reason. */}
      <RefusalNotice lastError={state.lastError} />
      {/* A prompt-vote round runs its own phase machine, so it drives the whole screen: `closed`
          arrives only at reveal, which is a phase we still need to render. */}
      {q?.type === "promptvote" && (
        <>
          <ReadOnlyNotice question={q} />
          <PromptVote prompt={q.prompt} aggregate={state.aggregate} lastError={state.lastError === ROUND_CLOSED_UNSENT ? null : state.lastError} readOnly={q.readOnly === true} send={send} />
        </>
      )}
      {/* A draw asks nothing of the phone, so this replaces the answer UI rather than sitting beside
          it — and it renders past `closed`, because the result IS what arrives at close. */}
      {q?.type === "picker" && state.aggregate?.type === "picker" && (
        <PickerView aggregate={state.aggregate} nickname={props.nickname} />
      )}
      {q && q.type !== "promptvote" && q.type !== "picker" && state.closed && (
        <p className="text-body-lg font-bold">{t("common.closed")}</p>
      )}
      {q && q.type !== "promptvote" && q.type !== "picker" && !state.closed && (
        <>
          <PrivateBadge question={q} />
          <ReadOnlyNotice question={q} />
          <h2 className="font-display text-display-sm">{q.prompt}</h2>
          {/* A DATE in this phone's own timezone, never a five-day countdown (W11, AC6). Rendered
              only when the room put an instant on the frame, which it does only for a long-running
              room's timed round. */}
          <CloseDate atMs={q.closesAtMs} />
          {/* A priority round asks for points or an order, not a tap — and it stays answerable after
              the first send, because the server takes the last write until the round closes. */}
          {q.type === "mc" && q.payload !== undefined && q.payload !== "one" && (
            <PriorityAnswer
              key={q.id}
              payload={q.payload}
              options={q.options ?? []}
              budget={q.budget ?? 0}
              readOnly={q.readOnly === true}
              send={(msg) => {
                if (send(msg)) props.setSubmitted(true);
              }}
            />
          )}
          {q.type === "mc" &&
            (q.payload === undefined || q.payload === "one") &&
            q.options?.map((o) => (
              <button
                key={o.id}
                disabled={props.submitted || q.readOnly === true}
                onClick={() => {
                  if (send({ t: "submit_mc", optionId: o.id })) props.setSubmitted(true);
                }}
                className="rounded-md bg-secondary p-4 text-left font-bold text-secondary-foreground disabled:opacity-50"
              >
                {o.label}
              </button>
            ))}
          {q.type === "wordcloud" && (
            <>
              <div className="flex gap-2">
                <input value={word} onChange={(e) => setWord(e.target.value)} maxLength={TEXT_MAX.answer} disabled={q.readOnly === true} placeholder={t("participant.typeWord")} className="flex-1 rounded-md border border-border p-3 disabled:opacity-50" />
                <button
                  onClick={() => {
                    // Cleared only when the queue actually took it: a refusal at the cap must leave
                    // the word in the box, or the "refused" of AC1 is a "discarded" with a sentence.
                    const text = word.trim();
                    if (!text || send({ t: "submit_words", text })) setWord("");
                  }}
                  disabled={q.readOnly === true}
                  className="rounded-md bg-primary px-4 font-bold text-primary-foreground disabled:opacity-50"
                >
                  {t("participant.send")}
                </button>
              </div>
              <CharCount value={word} max={TEXT_MAX.answer} />
              {/* "Restam 2 de 3 palavras" (US-LV-20 AC1). The input above stays ENABLED at zero on
                  purpose — this number is read off the phone's own queue and can be one high, so
                  the server's refusal (rendered by `RefusalNotice`) is the gate and this is the
                  hint. Same relationship `remainingBudget` has with a priority round. */}
              <WordQuota max={q.maxWords} used={wordsUsed(state.pending, q.id)} />
            </>
          )}
          {q.type === "open" && (
            <>
              {/* The consent line of a NAMED panel, before the first submit (US-LV-20 AC3): the
                  nickname will stand next to the sentence for the whole room. A code from the
                  server's config, rendered in this phone's own language (#6) — never copy on the
                  wire. Nothing at all on an anonymous round. */}
              {q.named === true && (
                <p className="rounded-md bg-surface p-2 font-bold">
                  <span aria-hidden="true">🙋 </span>
                  {t("panel.namedNotice")}
                </p>
              )}
              <div className="flex gap-2">
                <input value={answer} onChange={(e) => setAnswer(e.target.value)} maxLength={TEXT_MAX.question} disabled={q.readOnly === true} placeholder={t("participant.typeAnswer")} className="flex-1 rounded-md border border-border p-3 disabled:opacity-50" />
                <button
                  onClick={() => {
                    const text = answer.trim();
                    if (!text || send({ t: "submit_open", text })) setAnswer("");
                  }}
                  disabled={q.readOnly === true}
                  className="rounded-md bg-primary px-4 font-bold text-primary-foreground disabled:opacity-50"
                >
                  {t("participant.send")}
                </button>
              </div>
              <CharCount value={answer} max={TEXT_MAX.question} />
              <WithheldNote question={q} closed={state.closed} aggregate={state.aggregate} />
              {state.aggregate?.type === "open" &&
                state.aggregate.entries.map((e) => (
                  <div key={e.id} className="flex items-center justify-between rounded-md bg-surface p-3">
                    <span>
                      <OpenEntryText entry={e} />
                    </span>
                    <button onClick={() => send({ t: "upvote", entryId: e.id })} className="rounded-md bg-secondary px-3 py-1 text-secondary-foreground">
                      ▲ {e.upvotes}
                    </button>
                  </div>
                ))}
            </>
          )}
        </>
      )}
      {/* While connected the client genuinely cannot tell landed from buffered, so "enviado" stays
          exactly as it was — and steps aside the moment the queue has something honest to say. */}
      {props.submitted && !banner && <p className="bg-success rounded-md p-2 text-center text-primary-foreground">{t("participant.submitted")}</p>}
      {banner && (
        <p className="rounded-md bg-surface p-2 text-center text-body-sm font-bold">
          {t(banner.key, { n: String(banner.n) })}
        </p>
      )}
      {/* The round ended with answers still on the phone. Said out loud rather than discarded in
          silence — the same principle as W10a's `omitted: n`. */}
      {state.lastError === ROUND_CLOSED_UNSENT && <p className="text-accent">{t("participant.unsentClosed")}</p>}
      {/* Homework that has been handed in (W11): what a phone needs after answering a round that
          closes on Friday is not "enviado", it is when to come back and by which link. */}
      {props.longRunning && props.submitted && !banner && (
        <p className="text-body-sm text-muted">{t("round.answeredComeBack")}</p>
      )}
      {state.leaderboard.length > 0 && <Leaderboard entries={state.leaderboard} />}
      {/* The exit for a seat that outlives the tab. Last on the screen and quiet, because it is a
          way out and not an action anybody should take by accident. */}
      {props.longRunning && (
        <button
          onClick={() => {
            clearQueue();
            onExit();
          }}
          className="self-center text-body-sm text-muted underline"
        >
          {t("participant.leaveRoom")}
        </button>
      )}
    </Shell>
  );
}

/**
 * THE refusal line — one renderer for every round type (US-LV-20 AC1/AC2).
 *
 * The bug this closes: this paragraph used to live INSIDE `PromptVote`, so `lastError` reached the
 * screen only on a prompt-vote round. The reducer set it on every type — a rejected word, a
 * second Q&A entry on a one-entry round, an `mc` answer for the wrong payload — and the phone
 * rendered the empty box and nothing else. Fixed at the root: one line, above the type branches,
 * where every refusal already arrives, rather than one copy per answer form.
 *
 * `ROUND_CLOSED_UNSENT` is excluded because it has its OWN sentence further down (it is about the
 * queue, not about a submission), and seat-level codes never reach a render — the effect that owns
 * the seat unmounts this screen first.
 */
export function RefusalNotice({ lastError }: { lastError: string | null }): React.ReactElement {
  if (lastError === null || lastError === ROUND_CLOSED_UNSENT) return <></>;
  return (
    <p role="alert" className="rounded-md bg-warning p-2 font-bold text-primary-foreground">
      {promptVoteError(lastError)}
    </p>
  );
}

/**
 * "Restam 2 de 3 palavras" (US-LV-20 AC1). Nothing at all on an unlimited round, which is every
 * word cloud shipped before this story.
 *
 * Clamped at zero rather than going negative: `used` is read off the phone's own queue and may run
 * one high (see `wordsUsed`), and "restam -1 palavras" is a bug report, not a sentence.
 */
/**
 * How much of the text cap this box has left (US-LV-20 AC4, round-1 review item K).
 *
 * The `maxLength` on the input is the hard stop — the browser's own, so nothing is typed that the
 * server would clamp. This is the visible half, and it is the half that was missing: a box that
 * stops taking letters with no number on screen reads as a broken keyboard, and a sentence that
 * came back 30 characters shorter than it was typed reads as a bug in the product.
 *
 * Digits only, so there is no fourth i18n key to keep in sync — and no `aria-live`, because a
 * counter announced on every keystroke is the noise `announce.ts` exists to avoid.
 */
export function CharCount({ value, max }: { value: string; max: number }): React.ReactElement {
  return (
    <p className="text-body-sm text-muted">
      {value.length}/{max}
    </p>
  );
}

export function WordQuota({ max, used }: { max: number | undefined; used: number }): React.ReactElement {
  if (max === undefined) return <></>;
  return (
    <p className="text-body-sm text-muted">
      {t("wc.quota", { n: String(Math.max(max - used, 0)), max: String(max) })}
    </p>
  );
}

/**
 * "Aberta até 12 de set., 18:00" (W11, US-LV-19 AC6).
 *
 * Renders nothing at all when the round carries no closing instant — an ordinary timed round is
 * every round shipped before this one, and it says nothing new.
 */
function CloseDate({ atMs }: { atMs: number | undefined }): React.ReactElement {
  const date = closesOn(atMs, getLocale());
  if (date === null) return <></>;
  return <p className="text-body-sm text-muted">{t("round.openUntil", { date })}</p>;
}

/**
 * Points a participant has not placed yet (W9a, US-LV-16).
 *
 * Pure and exported for the same reason the host panels' validators are: it is the one number on the
 * screen and the phone's only guard against sending a sum the server will refuse.
 */
export function remainingBudget(weights: Readonly<Record<string, number>>, budget: number): number {
  return budget - Object.values(weights).reduce((a, b) => a + b, 0);
}

/**
 * Move one row of a ranking (W9a). Out of range is the IDENTITY, not a throw: the ↑/↓ at the ends
 * are disabled, and a swap that fell off the list would drop an option out of the permutation the
 * server requires — a refusal the person could not see the cause of.
 */
export function moveInOrder(order: readonly string[], index: number, delta: -1 | 1): string[] {
  const to = index + delta;
  if (index < 0 || index >= order.length || to < 0 || to >= order.length) return [...order];
  const next = [...order];
  [next[index], next[to]] = [next[to]!, next[index]!];
  return next;
}

/**
 * The phone's half of a priority round (AC6): −/+ over a budget, or ↑/↓ over a ranking.
 *
 * One `submit_mc` per tap of Enviar and never a partial state — the whole allocation or the whole
 * permutation, because the server judges it whole (a sum, or a permutation of every option) and a
 * per-tap send would be a stream of refusals while somebody is still deciding.
 *
 * No drag and no library: drag-and-drop on a phone, in a room, over venue wifi, with a screen
 * reader in the audience, is four ways to lose an answer. Two buttons work in all four.
 */
export function PriorityAnswer({
  payload,
  options,
  budget,
  readOnly,
  send,
}: {
  payload: "weighted" | "ordered";
  options: ReadonlyArray<{ id: string; label: string }>;
  budget: number;
  readOnly: boolean;
  send: (msg: { t: "submit_mc"; weights: Record<string, number> } | { t: "submit_mc"; order: string[] }) => void;
}): React.ReactElement {
  const [weights, setWeights] = React.useState<Record<string, number>>({});
  const [order, setOrder] = React.useState<string[]>(() => options.map((o) => o.id));
  // What the last ↑/↓ did, for the live region. A ranking has no visible line that changes when a
  // row moves — the list simply reorders — so without this a screen-reader user presses a button
  // and hears nothing at all.
  const [moved, setMoved] = React.useState("");
  const remaining = remainingBudget(weights, budget);
  const labelOf = (id: string): string => options.find((o) => o.id === id)?.label ?? id;
  const bump = (id: string, by: 1 | -1): void =>
    setWeights((prev) => ({ ...prev, [id]: Math.max(0, (prev[id] ?? 0) + by) }));
  const move = (i: number, by: -1 | 1): void => {
    setOrder(moveInOrder(order, i, by));
    setMoved(t("prio.moved", { label: labelOf(order[i]!), i: String(i + by + 1), n: String(order.length) }));
  };
  /** The stepper's accessible name carries the CURRENT count: a button whose label never changes
   *  gives no confirmation that the press landed, and "dim" is not an announcement. */
  const stepper = (key: "prio.less" | "prio.more", label: string, n: number): string =>
    t(n === 1 ? `${key}One` : key, { label, n: String(n) });

  // `fieldset[disabled]` is the whole read-only story (W7): the platform disables every control
  // inside it, so "this round is not taking your answer" is ONE attribute rather than the same
  // clause repeated on seven buttons — where the seventh is the one somebody forgets.
  if (payload === "weighted") {
    return (
      <fieldset disabled={readOnly} className="flex flex-col gap-2 border-0 p-0">
        {options.map((o) => (
          <div key={o.id} className="flex items-center justify-between gap-2 rounded-md bg-surface p-3">
            <span className="flex-1">{o.label}</span>
            <button
              onClick={() => bump(o.id, -1)}
              disabled={(weights[o.id] ?? 0) === 0}
              aria-label={stepper("prio.less", o.label, weights[o.id] ?? 0)}
              className="rounded-md bg-secondary px-3 py-1 font-bold text-secondary-foreground disabled:opacity-50"
            >
              −
            </button>
            <span className="w-8 text-center font-bold">{weights[o.id] ?? 0}</span>
            <button
              onClick={() => bump(o.id, 1)}
              disabled={remaining === 0}
              aria-label={stepper("prio.more", o.label, weights[o.id] ?? 0)}
              className="rounded-md bg-secondary px-3 py-1 font-bold text-secondary-foreground disabled:opacity-50"
            >
              +
            </button>
          </div>
        ))}
        {/* The one line that changes on every press, and the only place the budget running out is
            announced rather than shown — `disabled` is not an event a screen reader reports. */}
        <p aria-live="polite" className="text-sm text-muted">
          {t("prio.remaining", { n: String(remaining), b: String(budget) })}
        </p>
        <button
          onClick={() => send({ t: "submit_mc", weights })}
          disabled={remaining === budget}
          className="rounded-md bg-primary p-3 font-bold text-primary-foreground disabled:opacity-50"
        >
          {t("participant.send")}
        </button>
      </fieldset>
    );
  }

  return (
    <fieldset disabled={readOnly} className="flex flex-col gap-2 border-0 p-0">
      <LiveRegion text={moved} label={t("prio.title")} />
      {order.map((id, i) => (
        <div key={id} className="flex items-center justify-between gap-2 rounded-md bg-surface p-3">
          <span className="flex-1">
            {i + 1}. {labelOf(id)}
          </span>
          <button
            onClick={() => move(i, -1)}
            disabled={i === 0}
            aria-label={t("prio.up", { label: labelOf(id) })}
            className="rounded-md bg-secondary px-3 py-1 font-bold text-secondary-foreground disabled:opacity-50"
          >
            ↑
          </button>
          <button
            onClick={() => move(i, 1)}
            disabled={i === order.length - 1}
            aria-label={t("prio.down", { label: labelOf(id) })}
            className="rounded-md bg-secondary px-3 py-1 font-bold text-secondary-foreground disabled:opacity-50"
          >
            ↓
          </button>
        </div>
      ))}
      <button
        onClick={() => send({ t: "submit_mc", order })}
        className="rounded-md bg-primary p-3 font-bold text-primary-foreground disabled:opacity-50"
      >
        {t("participant.send")}
      </button>
    </fieldset>
  );
}

/**
 * Prompt-vote round (US-LV-10): write → vote → reveal. The phase comes from the server's aggregate,
 * never from a local guess, so a reconnecting phone lands on whatever phase the room is actually in.
 * There is no success acknowledgement on the wire, so "sent"/"voted" are local optimism — the server
 * remains the authority and refuses anything invalid with a code we render below.
 */
export function PromptVote({
  prompt,
  aggregate,
  lastError,
  readOnly = false,
  send,
}: {
  prompt: string;
  aggregate: QuestionAggregate | null;
  lastError: string | null;
  /** The round named who may answer and it was not this phone (W7). Both phases are read-only:
   *  the server refuses `submit_prompt` AND `vote_prompt`, so leaving the ballot live would offer a
   *  button whose only possible outcome is an error. */
  readOnly?: boolean;
  send: (m: { t: "submit_prompt"; text: string } | { t: "vote_prompt"; entryId: string }) => void;
}): React.ReactElement {
  const [draft, setDraft] = React.useState("");
  const [sent, setSent] = React.useState(false);
  const [votedId, setVotedId] = React.useState<string | null>(null);
  const pv = aggregate?.type === "promptvote" ? aggregate : null;

  // A refusal means the attempt did not land, so let them try again.
  React.useEffect(() => {
    if (lastError) {
      setSent(false);
      setVotedId(null);
    }
  }, [lastError]);

  // The SENTENCE is rendered by `RefusalNotice`, once, for every round type — this component only
  // needs to know THAT a refusal happened, so the local optimism above can be taken back. Two
  // copies of the paragraph would have put the same warning twice on a prompt-vote round.
  const error = lastError !== null;

  return (
    <div className="flex flex-col gap-3">
      <h2 className="font-display text-display-sm">{prompt}</h2>

      {(!pv || pv.phase === "write") && (
        <>
          <p className="text-sm text-muted">{t("pv.writeHint")}</p>
          <div className="flex gap-2">
            <input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submit()}
              placeholder={t("pv.write")}
              disabled={readOnly}
              className="flex-1 rounded-md border border-border p-3 disabled:opacity-50"
              maxLength={TEXT_MAX.answer}
              autoFocus
            />
            <button onClick={submit} disabled={readOnly} className="rounded-md bg-primary px-4 font-bold text-primary-foreground disabled:opacity-50">
              {t("participant.send")}
            </button>
          </div>
          {sent && !error && (
            <p className="rounded-md bg-success p-2 text-center text-primary-foreground">{t("pv.sent")}</p>
          )}
        </>
      )}

      {pv?.phase === "vote" && (
        <>
          <h3 className="font-bold">{t("pv.vote")}</h3>
          <p className="text-sm text-muted">{t("pv.voteHint")}</p>
          {/* The screen this sentence exists for: the author whose line is not on this ballot reads
              that it was a draw, instead of assuming the room threw it out. */}
          <OmittedNote n={pv.omitted} />
          {pv.ballot.map((entry) => (
            <button
              key={entry.id}
              disabled={votedId !== null || readOnly}
              onClick={() => {
                send({ t: "vote_prompt", entryId: entry.id });
                setVotedId(entry.id);
              }}
              className={`rounded-md p-4 text-left font-bold disabled:opacity-50 ${
                votedId === entry.id ? "bg-success text-primary-foreground" : "bg-secondary text-secondary-foreground"
              }`}
            >
              {entry.text}
            </button>
          ))}
          {votedId && !error && <p className="text-center font-bold">{t("pv.voted")}</p>}
        </>
      )}

      {pv?.phase === "reveal" && (
        <>
          <h3 className="font-bold">{t("pv.reveal")}</h3>
          <OmittedNote n={pv.omitted} />
          {revealRows(pv).map((r) => (
            <div key={r.id} className="flex items-center justify-between rounded-md bg-surface p-3">
              <span>
                {r.text}
                {r.author !== null && <> — <b>{r.author}</b></>}
              </span>
              <span className="font-bold">
                {r.votes} {t(r.votes === 1 ? "pv.voteOne" : "pv.votes")}
              </span>
            </div>
          ))}
        </>
      )}
    </div>
  );

  function submit(): void {
    const text = draft.trim();
    if (!text) return;
    send({ t: "submit_prompt", text });
    setSent(true);
  }
}

/**
 * "🔒 Só você vê isso" — this round was addressed to this phone and no other (W6a, US-LV-13).
 *
 * The flag comes off the payload the server built for THIS socket (`toPublicQuestion` sets it only
 * on a merged variant), never from anything the client infers — so the badge cannot promise a
 * privacy the wire did not actually give. Exported for the same reason `PickerView` is: that rule
 * is the whole component, and it is worth one assertion.
 */
export function PrivateBadge({ question }: { question: { private?: true } }): React.ReactElement {
  if (!question.private) return <></>;
  return (
    // No aria-label here: naming is prohibited on the paragraph role, so AT would ignore it and read
    // the emoji anyway. Hiding the lock from AT is what makes the sentence the only thing announced.
    <p className="rounded-md bg-accent p-2 font-bold text-primary-foreground">
      <span aria-hidden="true">🔒 </span>
      {t("target.private")}
    </p>
  );
}

/**
 * A refusal code, turned into a sentence a participant can read.
 *
 * `t` returns the KEY when nothing matches, so an unmapped code used to render as
 * "pv.err.NOT_ELIGIBLE" on a phone — which is how the W7 code shipped, and how NOT_JOINED, REJECTED
 * and BAD_JSON have always rendered. Named codes still get their own sentence; the guard is for the
 * long tail, so that adding a refusal server-side can never again put an identifier on a screen.
 *
 * The NAME and the `pv.err.` key prefix are now historical: since US-LV-20 this is the mapper for
 * every round type (see `RefusalNotice`), not just prompt-vote. Renaming ten keys across three
 * locales would be churn with no behaviour behind it, and the fallback above is what actually makes
 * the table safe to grow.
 */
export function promptVoteError(code: string): string {
  const text = t(`pv.err.${code}`);
  return text === `pv.err.${code}` ? t("pv.err.fallback") : text;
}

/**
 * "Só quem foi escolhido responde esta rodada" — this round is not taking this phone's answer (W7).
 *
 * Driven by the payload's own `readOnly`, exactly as the private badge is driven by `private`: the
 * server decided, and the phone renders what it was told. Disabling the controls beside it is a
 * courtesy — the socket answers `NOT_ELIGIBLE` whatever this screen chooses to show. The upvote
 * buttons below are deliberately NOT disabled: an upvote is not a submission, which is the whole
 * point of the fishbowl's outer circle.
 */
export function ReadOnlyNotice({ question }: { question: { readOnly?: true; hosted?: true } }): React.ReactElement {
  if (!question.readOnly) return <></>;
  return (
    <p className="rounded-md bg-surface p-2 font-bold">
      <span aria-hidden="true">🙋 </span>
      {/* A single-screen round (US-LV-22) is read-only for EVERYONE: "only the chosen answer" would be false. */}
      {t(question.hosted === true ? "hosted.phone" : "teams.readOnly")}
    </p>
  );
}

/**
 * "Seu time: Águias" + where this phone stands in it (W7, US-LV-14).
 *
 * Both facts arrive addressed to this socket alone and neither is derivable from anything broadcast —
 * which is why the chip can be shown in a room running SECRET membership without breaking it: it says
 * what this person's own team is, never anyone else's.
 */
export function TeamStatus({ team, board }: { team: { id: string; name: string } | null; board: MyBoard | null }): React.ReactElement {
  if (!team) return <></>;
  return (
    <div className="flex flex-col gap-1">
      <p className="bg-secondary rounded-md p-2 text-center font-bold text-secondary-foreground">
        {t("teams.yourTeam", { name: team.name })}
      </p>
      {board && (
        <p className="text-center text-sm text-muted">
          {t("teams.myBoard", {
            r: String(board.rankInTeam),
            n: String(board.teamSize),
            tr: String(board.teamRank),
            tc: String(board.teamCount),
          })}
        </p>
      )}
    </div>
  );
}

/**
 * A draw, on the phone of someone in the room.
 *
 * The one thing this screen is for is answering "was it me?" without making them read the projector.
 * Matched on nickname, which is what the wire carries — nicknames are not unique and nothing dedupes
 * them, so two people called "Ana" both see "Foi você!". That is a known limit of having no public
 * participant id; roadmap W6 mints one and this is one of the five features waiting on it.
 */
export function PickerView({
  aggregate,
  nickname,
}: {
  aggregate: Extract<QuestionAggregate, { type: "picker" }>;
  nickname: string;
}): React.ReactElement {
  if (aggregate.phase === "spinning") {
    return <p className="text-display-sm font-bold">🎲 {t("picker.spinning")}</p>;
  }
  if (aggregate.pick === "teams") {
    const mine = aggregate.teams.findIndex((team) => team.includes(nickname));
    return (
      <div className="flex flex-col gap-2">
        {mine >= 0 && (
          <p className="bg-success rounded-md p-3 text-center font-bold text-primary-foreground">
            {t("picker.yourTeam", { n: String(mine + 1) })}
          </p>
        )}
        {aggregate.teams.map((team, i) => (
          <div key={i} className="rounded-md bg-surface p-3">
            <b>
              {t("picker.team")} {i + 1}
            </b>
            : {team.join(", ")}
          </div>
        ))}
      </div>
    );
  }
  if (aggregate.pick === "rotation") {
    const mine = aggregate.order.indexOf(nickname);
    return (
      <div className="flex flex-col gap-2">
        {mine >= 0 && (
          <p className="bg-success rounded-md p-3 text-center font-bold text-primary-foreground">
            {t("picker.yourPosition", { n: String(mine + 1) })}
          </p>
        )}
        <div className="rounded-md bg-surface p-3">{aggregate.order.map((n, i) => `${i + 1}. ${n}`).join(" · ")}</div>
      </div>
    );
  }
  const isMe = aggregate.drawn !== null && aggregate.drawn === nickname;
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted">{t("picker.drawn")}</p>
      <p className={`rounded-md p-3 text-center font-display text-display-sm ${isMe ? "bg-success text-primary-foreground" : "bg-surface"}`}>
        {aggregate.drawn ?? t("picker.nobody")}
      </p>
      {isMe && <p className="text-center font-bold">{t("picker.you")}</p>}
    </div>
  );
}

function Leaderboard({ entries }: { entries: Array<{ nickname: string; score: number; rank: number }> }): React.ReactElement {
  return (
    <div className="flex flex-col gap-1">
      <h3 className="font-display text-display-sm">{t("common.leaderboard")}</h3>
      {entries.slice(0, 10).map((e, i) => (
        <div key={i} className="flex justify-between rounded-md bg-surface p-2">
          <span>
            {e.rank}. {e.nickname}
          </span>
          <span className="font-bold">{e.score}</span>
        </div>
      ))}
    </div>
  );
}
